/**
 * Widget Template System
 *
 * This module provides factory functions to create widget definitions
 * with consistent structure and data fetching configuration.
 *
 * Each widget template includes:
 * - Identity: id, name, description, icon, category
 * - Display: type, defaultSize
 * - Data: dataSource, getData function (for API call + transform)
 * - Behavior: supportsDateFilter, refreshRate
 *
 * Features:
 * - Input validation for required fields
 * - Safe getData wrapper for error handling
 * - Type constants for consistency
 */

import { IconType } from 'react-icons';
import {
  FiActivity,
  FiDollarSign,
  FiClock,
  FiAlertTriangle,
  FiCheckCircle,
  FiTrendingUp,
  FiCpu,
  FiDatabase,
  FiCloud,
  FiShield,
  FiUsers,
  FiZap,
  FiBarChart2,
  FiPieChart,
  FiList,
  FiServer,
  FiGitBranch,
  FiMessageSquare,
  FiBell,
  FiFolder,
  FiCalendar,
  FiPercent,
  FiHash,
  FiLayers,
  FiPlay,
  FiPause,
  FiXCircle,
  FiEye,
  FiTerminal,
  FiBox,
  FiGrid,
  FiClipboard,
  FiAward,
  FiRefreshCw,
  FiUser,
  FiSettings,
  FiTarget,
  FiArrowUp,
  FiArrowDown,
  FiMaximize2,
  FiTag,
  FiFileText,
  FiLock,
  FiPackage,
  FiLifeBuoy,
  FiAlertOctagon,
  FiHeart,
  FiAlertCircle,
  FiBriefcase,
  FiMail,
  FiTool,
  FiArchive,
  FiCrop,
  FiMoon,
} from 'react-icons/fi';

export interface WidgetSize {
  w: number;
  h: number;
  minW: number;
  minH: number;
}

export interface WidgetTemplate {
  id: string;
  name: string;
  description: string;
  icon: IconType;
  category: string;
  type: string;
  defaultSize: WidgetSize;
  dataSource: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Core widget interface used by dozens of widget files
  getData: (sourceData: any, context?: any) => any;
  supportsDateFilter: boolean;
  refreshRate: number;
  respectsClientFilter?: boolean;
}

export type WidgetTemplateConfig = {
  id: string;
  name: string;
  description?: string;
  icon?: IconType;
  category: string;
  type?: string;
  defaultSize?: WidgetSize;
  dataSource: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Core widget interface used by dozens of widget files
  getData: (sourceData: any, context?: any) => any;
  supportsDateFilter?: boolean;
  refreshRate?: number;
  respectsClientFilter?: boolean;
};

// Widget Types
export const WIDGET_TYPES = {
  METRIC: 'metric',
  CHART: 'chart',
  LIST: 'list',
  TABLE: 'table',
  CUSTOM: 'custom',
};

// Widget Categories
export const WIDGET_CATEGORIES = {
  HUB: 'Hub',
  HEALTH: 'Health',
  AWS_INSIGHTS: 'AWS Insights',
  PLATFORMOPS: 'CloudOps',
  DATAOPS: 'DataOps',
  GENAIOPS: 'GenAIOps',
  AGENTOPS: 'AgentOps',
  CUSTOM: 'Custom',
};

// Widget Sizes (react-grid-layout format)
export const WIDGET_SIZES = {
  SMALL: { w: 1, h: 2, minW: 1, minH: 2 },
  MEDIUM: { w: 2, h: 2, minW: 1, minH: 2 },
  LARGE: { w: 3, h: 2, minW: 2, minH: 2 },
  TALL: { w: 1, h: 4, minW: 1, minH: 2 },
  WIDE: { w: 4, h: 2, minW: 2, minH: 2 },
  FULL: { w: 4, h: 4, minW: 2, minH: 2 },
};

// Data sources (for grouping API calls)
export const DATA_SOURCES = {
  HUB: 'hub',
  HEALTH: 'health',
  AWS_INSIGHTS: 'aws_insights',
  PLATFORMOPS: 'platformops',
  DATAOPS: 'dataops',
  GENAIOPS: 'genaiops',
  AGENTOPS: 'agentops',
};

/**
 * Validate required widget config fields
 * @param {Object} config - Widget configuration object
 * @param {string[]} requiredFields - Array of required field names
 * @throws {Error} if required fields are missing or invalid
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Validates dynamic config object
const validateWidgetConfig = (config: Record<string, any>, requiredFields: string[]): void => {
  const missing = requiredFields.filter(field => config[field] == null);
  if (missing.length > 0) {
    throw new Error(`Widget config missing required fields: ${missing.join(', ')}`);
  }

  // Validate specific field types
  if (config.id && typeof config.id !== 'string') {
    throw new Error(`Widget id must be a string, got ${typeof config.id}`);
  }
  if ((config.id?.length ?? 0) === 0) {
    throw new Error('Widget id cannot be empty');
  }
  if (config.name && typeof config.name !== 'string') {
    throw new Error(`Widget name must be a string, got ${typeof config.name}`);
  }
  if (config.getData && typeof config.getData !== 'function') {
    throw new Error(`Widget getData must be a function, got ${typeof config.getData}`);
  }
  if (config.refreshRate !== undefined && (typeof config.refreshRate !== 'number' || config.refreshRate < 0)) {
    throw new Error(`Widget refreshRate must be a positive number, got ${config.refreshRate}`);
  }
  if (config.type && !Object.values(WIDGET_TYPES).includes(config.type)) {
    throw new Error(`Invalid widget type "${config.type}" - expected: ${Object.values(WIDGET_TYPES).join('|')}`);
  }
  if (config.category && !Object.values(WIDGET_CATEGORIES).includes(config.category)) {
    throw new Error(`Invalid widget category "${config.category}" - expected: ${Object.values(WIDGET_CATEGORIES).join('|')}`);
  }
  if (config.dataSource && !Object.values(DATA_SOURCES).includes(config.dataSource)) {
    throw new Error(`Invalid data source "${config.dataSource}" - expected: ${Object.values(DATA_SOURCES).join('|')}`);
  }
};

/**
 * Wrap a getData function with error handling
 * Catches and logs errors to prevent widget crashes
 *
 * @param {Function} getData - The original getData function
 * @param {string} widgetId - Widget ID for error logging
 * @returns {Function} Wrapped getData function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Wraps generic getData signature
export const safeGetData = (getData: ((sourceData: any, context?: any) => any) | null | undefined, widgetId: string): (sourceData: any, context?: any) => any => {
  if (!getData || typeof getData !== 'function') {
    return () => null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (sourceData: any, context?: any) => {
    try {
      if (sourceData == null) {
        return null;
      }
      return getData(sourceData, context);
    } catch (error) {
      console.error(`Widget ${widgetId} getData error:`, error);
      return null;
    }
  };
};

/**
 * Base template factory
 * Creates a complete widget definition with all required properties
 *
 * @param {Object} config - Widget configuration
 * @param {string} config.id - Unique widget identifier
 * @param {string} config.name - Display name
 * @param {string} [config.description] - Widget description
 * @param {React.Component} [config.icon] - Icon component
 * @param {string} config.category - Widget category (from WIDGET_CATEGORIES)
 * @param {string} config.type - Widget type (from WIDGET_TYPES)
 * @param {Object} [config.defaultSize] - Default size (from WIDGET_SIZES)
 * @param {string} config.dataSource - Data source (from DATA_SOURCES)
 * @param {Function} config.getData - Data transformation function
 * @param {boolean} [config.supportsDateFilter] - Whether widget supports date filtering
 * @param {number} [config.refreshRate] - Auto-refresh rate in ms
 * @returns {Object} Complete widget definition
 */
export const createWidgetTemplate = ({
  // Identity
  id,
  name,
  description = '',
  icon = FiActivity,
  category,

  // Display
  type,
  defaultSize = WIDGET_SIZES.SMALL,

  // Data
  dataSource,
  getData, // async (sourceData, context) => widgetData

  // Behavior
  supportsDateFilter = true,
  refreshRate = 60000,
  respectsClientFilter,
}: WidgetTemplateConfig): WidgetTemplate => {
  // Validate required fields
  validateWidgetConfig(
    { id, name, category, type, dataSource, getData },
    ['id', 'name', 'category', 'type', 'dataSource', 'getData']
  );

  return {
    id,
    name,
    description,
    icon,
    category,
    type: type as string,
    defaultSize,
    dataSource,
    getData: safeGetData(getData, id),
    supportsDateFilter,
    refreshRate,
    ...(respectsClientFilter === undefined ? {} : { respectsClientFilter }),
  };
};

/**
 * Metric Widget Template
 * For displaying single KPI values with optional trends
 *
 * Expected data shape:
 * {
 *   value: number,
 *   label: string,
 *   format: 'number' | 'currency' | 'percentage' | 'duration',
 *   color: string (tailwind color name),
 *   trend?: 'up' | 'down',
 *   trendValue?: string,
 *   displayValue?: string (override display)
 * }
 */
export const createMetricWidget = (config: Omit<WidgetTemplateConfig, 'type'>): WidgetTemplate =>
  createWidgetTemplate({
    ...config,
    type: WIDGET_TYPES.METRIC,
    defaultSize: config.defaultSize || WIDGET_SIZES.SMALL,
  });

/**
 * Chart Widget Template
 * For displaying charts and visualizations
 *
 * Expected data shape:
 * {
 *   chartType: 'bar' | 'line' | 'area' | 'pie' | 'donut' | 'horizontalBar',
 *   data: Array<object>,
 *   dataKey: string (for pie/donut),
 *   xKey: string (for bar/line),
 *   yKey: string (for bar/line),
 *   color: string (hex color),
 *   colors?: Array<string> (palette override)
 * }
 */
export const createChartWidget = (config: Omit<WidgetTemplateConfig, 'type'>): WidgetTemplate =>
  createWidgetTemplate({
    ...config,
    type: WIDGET_TYPES.CHART,
    defaultSize: config.defaultSize || WIDGET_SIZES.MEDIUM,
  });

/**
 * List Widget Template
 * For displaying item lists with badges and actions
 *
 * Expected data shape:
 * {
 *   items: Array<{
 *     icon?: Component,
 *     title: string,
 *     subtitle?: string,
 *     description?: string,
 *     value?: string | number,
 *     timestamp?: string,
 *     badge?: { text: string, color: string }
 *   }>,
 *   maxItems?: number,
 *   showBadge?: boolean
 * }
 */
export const createListWidget = (config: Omit<WidgetTemplateConfig, 'type'>): WidgetTemplate =>
  createWidgetTemplate({
    ...config,
    type: WIDGET_TYPES.LIST,
    defaultSize: config.defaultSize || WIDGET_SIZES.MEDIUM,
  });

// Export icons for widget definitions
export const WIDGET_ICONS = {
  FiActivity,
  FiDollarSign,
  FiClock,
  FiAlertTriangle,
  FiCheckCircle,
  FiTrendingUp,
  FiCpu,
  FiDatabase,
  FiCloud,
  FiShield,
  FiUsers,
  FiZap,
  FiBarChart2,
  FiPieChart,
  FiList,
  FiServer,
  FiGitBranch,
  FiMessageSquare,
  FiBell,
  FiFolder,
  FiCalendar,
  FiPercent,
  FiHash,
  FiLayers,
  FiPlay,
  FiPause,
  FiXCircle,
  FiEye,
  FiTerminal,
  FiBox,
  FiGrid,
  FiClipboard,
  FiAward,
  FiRefreshCw,
  FiUser,
  FiSettings,
  FiTarget,
  FiArrowUp,
  FiArrowDown,
  FiMaximize2,
  FiTag,
  FiFileText,
  FiLock,
  FiPackage,
  FiLifeBuoy,
  FiAlertOctagon,
  FiHeart,
  FiAlertCircle,
  FiBriefcase,
  FiMail,
  FiTool,
  FiArchive,
  FiCrop,
  FiMoon,
};
